import { ensureAmplitudeInitialized, identifyAmplitude, trackAmplitude } from './providers/amplitude-provider';
import { ensureMixpanelInitialized, identifyMixpanel, trackMixpanel } from './providers/mixpanel-provider';
import type { AnalyticsUser, EventProps, SuperProperties } from './types';

/**
 * 統一トラッキングファサード。
 *
 * 呼び出し側は ensureAnalyticsInitialized / initAnalytics / trackEvent を使い、
 * 内部で Mixpanel と Amplitude の両 provider に配る。
 * 片方の provider が失敗してももう片方を巻き込まないよう個別に try/catch する。
 *
 * ライフサイクルは「SDK の匿名初期化」と「ユーザー識別」を分離している:
 * - ensureAnalyticsInitialized(): ログイン前でも安全に track できるよう SDK を初期化する。
 *   Mixpanel は init 前に track すると例外になるため、これが先行する必要がある。
 * - initAnalytics(user): ログイン後にユーザー識別・属性設定を行う（内部で ensure も実行）。
 */

let identified = false;
// Amplitude にはグローバル register 相当が無いため、init 時に検知した
// スーパープロパティを保持し、全 trackEvent にマージして送る。
let superProperties: SuperProperties | null = null;

/**
 * トラッキング SDK を匿名で初期化する。ログイン前イベントの送信に備えて早期に呼ぶ。
 * 各 provider 側で二重初期化を防止しているため、何度呼んでも安全。
 */
export function ensureAnalyticsInitialized(): void {
  const mixpanelSuperProps = safeRun(ensureMixpanelInitialized, 'Mixpanel init');
  const amplitudeSuperProps = safeRun(ensureAmplitudeInitialized, 'Amplitude init');
  // Mixpanel / Amplitude は同一の検知ロジックを使うため、どちらかの値を採用すれば十分。
  superProperties = mixpanelSuperProps ?? amplitudeSuperProps ?? superProperties;
}

/**
 * サインイン済みユーザーでトラッキングを初期化（SDK 初期化 + ユーザー識別）する。
 * ユーザー識別はモジュールレベルのフラグにより1回だけ実行される。
 */
export function initAnalytics(user: AnalyticsUser): void {
  if (!user.id) return;

  // SDK 初期化は識別済みでも冪等に通す（ensure 自体が二重初期化を防ぐ）
  ensureAnalyticsInitialized();

  if (identified) return;
  identified = true;

  const mixpanelSuperProps = safeRun(() => identifyMixpanel(user), 'Mixpanel identify');
  const amplitudeSuperProps = safeRun(() => identifyAmplitude(user), 'Amplitude identify');
  superProperties = mixpanelSuperProps ?? amplitudeSuperProps ?? superProperties;
}

/**
 * イベントを Mixpanel と Amplitude の両方に送信する。
 * Amplitude にはスーパープロパティをマージして渡す（Mixpanel は register で自動付与）。
 */
export function trackEvent(eventName: string, properties?: EventProps): void {
  try {
    trackMixpanel(eventName, properties);
  } catch (e) {
    console.warn('[Analytics] Mixpanel track 失敗:', e);
  }

  try {
    const amplitudeProps = superProperties ? { ...superProperties, ...properties } : properties;
    trackAmplitude(eventName, amplitudeProps);
  } catch (e) {
    console.warn('[Analytics] Amplitude track 失敗:', e);
  }
}

/**
 * provider 呼び出しを例外安全に実行するヘルパ。
 */
function safeRun(fn: () => SuperProperties | null, label: string): SuperProperties | null {
  try {
    return fn();
  } catch (e) {
    console.warn(`[Analytics] ${label} に失敗:`, e);
    return null;
  }
}
